import { NearbyGeoCautionDto } from '@mark43/rms-api';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FeatureLayer, Graphic, WebMap } from '../constructors';
import { EntityIdToObjectIdMaps } from '../helpers/entity-id-to-object-id-maps';
import { createNearbyGeoCautionsFeatureLayer } from '../helpers/feature-layers';
import { createNearbyGeoCautionsGraphic } from '../helpers/graphics';
import { areLatLngsEqual } from '../../location/helpers';
import { GeoPoint } from '../../location/types';
import { NearbyGeoCautionDtoExtended } from '../types';
import { useLayerControl, usePreviousLayer } from './use-feature-layer-control';

// show nearby geo cautions icons on the map

type NearbyGeoCautionsFeatureLayerProps = {
    nearbyGeoCautions: NearbyGeoCautionDto[];
    selectedNearbyGeoCautionCoordinates?: GeoPoint;
    webMap?: WebMap;
};

export const useNearbyGeoCautionsFeatureLayer = ({
    nearbyGeoCautions,
    selectedNearbyGeoCautionCoordinates,
    webMap,
}: NearbyGeoCautionsFeatureLayerProps) => {
    const [nearbyGeoCautionsLayer, setNearbyGeoCautionsLayer] = useState<FeatureLayer>();

    const entityIdToObjectIdMapCacheRef = useRef<typeof EntityIdToObjectIdMaps.NEARBY_GEO_CAUTIONS>(
        EntityIdToObjectIdMaps.NEARBY_GEO_CAUTIONS
    );

    const nearbyGeoCautionsExtended = useMemo(() => {
        return nearbyGeoCautions.map((caution) => {
            return { ...caution, entityId: caution.id };
        });
    }, [nearbyGeoCautions]);

    const prevSelectedNearbyGeoCautionCoordinates = usePreviousLayer(
        selectedNearbyGeoCautionCoordinates,
        nearbyGeoCautionsLayer
    );

    const didNearbyGeoCautionsGraphicChange = useCallback(
        (currItem: NearbyGeoCautionDtoExtended, prevItem: NearbyGeoCautionDtoExtended) => {
            return (
                !areLatLngsEqual(currItem, prevItem) ||
                !isEqual(
                    selectedNearbyGeoCautionCoordinates,
                    prevSelectedNearbyGeoCautionCoordinates
                )
            );
        },
        [selectedNearbyGeoCautionCoordinates, prevSelectedNearbyGeoCautionCoordinates]
    );

    // handles initializing empty feature layer
    useEffect(() => {
        if (webMap) {
            const nearbyGeoCautionsFeatureLayer = createNearbyGeoCautionsFeatureLayer();
            setNearbyGeoCautionsLayer(nearbyGeoCautionsFeatureLayer);
            webMap.layers.add(nearbyGeoCautionsFeatureLayer);
        }
    }, [webMap]);

    const createNearbyGeoCautionsGraphics = useCallback(
        (nearbyGeoCautions: NearbyGeoCautionDtoExtended[]) => {
            const nearbyGeoCautionsGraphics: Graphic[] = [];
            for (const nearbyGeoCaution of nearbyGeoCautions) {
                const objectId = entityIdToObjectIdMapCacheRef.current.get(
                    nearbyGeoCaution.entityId
                );
                const nearbyGeoCautionsGraphic = createNearbyGeoCautionsGraphic({
                    objectId,
                    nearbyGeoCaution,
                    selectedNearbyGeoCautionCoordinates,
                });
                if (nearbyGeoCautionsGraphic) {
                    nearbyGeoCautionsGraphics.push(nearbyGeoCautionsGraphic);
                }
            }
            return nearbyGeoCautionsGraphics;
        },
        [selectedNearbyGeoCautionCoordinates]
    );

    useLayerControl<NearbyGeoCautionDtoExtended>({
        layer: nearbyGeoCautionsLayer,
        currItems: nearbyGeoCautionsExtended,
        didItemChange: didNearbyGeoCautionsGraphicChange,
        createGraphics: createNearbyGeoCautionsGraphics,
        entityIdToObjectIdMapCacheRef,
    });

    return { nearbyGeoCautionsLayer };
};
